import { useCallback, useEffect, useState } from 'react';
import { Content_Kind } from '@gen/chat/v1/common_pb';
import { useStrings } from '@/i18n/Localization';
import { fetchMedia, needsMeteredConfirmation, requestGrant, saveBlob } from '@/media/download';
import { formatBytes } from '@/media/format';
import { isPreviewable } from '@/media/uploads';
import { VoicePlayer } from '@/components/VoicePlayer';
import { useLiveQuery } from '@/store/hooks';
import { listMediaFor } from '@/store/localStore';
import type { Hex } from '@/store/ids';
import type { MediaRecord, StoredAttachment } from '@/store/types';
import type { WorkerClient } from '@/transport/workerClient';
import dialogStyles from '@/components/DeleteDialog.module.css';
import styles from '@/components/media.module.css';

export interface AttachmentViewProps {
  worker: WorkerClient | null;
  conversationId: Hex;
  messageId: string;
  attachments: readonly StoredAttachment[];
  /** Content kind, so an album is laid out as one. */
  kind: number;
  /** Section 06: protected conversations refuse client download and save. */
  protectedContent: boolean;
  /** Set when auto-advance has selected this message's voice note. */
  autoPlayVoice?: boolean;
  onVoicePosition?: (positionMs: number) => void;
}

/**
 * Attachments inside a message bubble.
 *
 * Section 14's rule about honesty governs the whole component: "Unknown formats
 * show filename, size, and download action. Never claim an arbitrary file is
 * previewable." An attachment therefore only renders as an image, a video or a
 * player when its MIME type is on the short allow-list in `media/uploads.ts`
 * *and* the server says the object is READY. Everything else — an unrecognized
 * type, an object still being processed, one whose decoder failed — renders as
 * a named row with a size and a download action.
 *
 * No content is ever inserted as HTML. SVG and HTML documents are deliberately
 * outside the previewable set, which is Section 06's "HTML/SVG documents
 * download as attachments rather than running inside the application origin"
 * expressed as the absence of a code path rather than as a filter.
 */
export function AttachmentView(props: AttachmentViewProps) {
  const mediaIds = props.attachments.map((attachment) => attachment.mediaId);
  const media = useLiveQuery<MediaRecord[]>(
    () => listMediaFor(mediaIds),
    [mediaIds.join(',')],
    [],
  );
  const byId = new Map(media.map((record) => [record.mediaId, record]));

  if (props.attachments.length === 0) {
    return null;
  }

  const album = props.kind === Content_Kind.ALBUM || props.attachments.length > 1;

  return (
    <div className={album ? styles.album : styles.attachments}>
      {props.attachments.map((attachment) => (
        <OneAttachment
          key={attachment.mediaId}
          {...props}
          attachment={attachment}
          media={byId.get(attachment.mediaId) ?? null}
        />
      ))}
    </div>
  );
}

function OneAttachment(
  props: AttachmentViewProps & { attachment: StoredAttachment; media: MediaRecord | null },
) {
  const { t } = useStrings();
  const { attachment, media, worker } = props;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    return () => {
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const ready = media === null || media.state.toUpperCase() === 'READY';
  const failedProcessing = media !== null && media.state.toUpperCase() === 'FAILED';
  const previewable = ready && !failedProcessing && isPreviewable(attachment.mime);

  const download = useCallback(
    async (save: boolean) => {
      if (!worker) {
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const result = await fetchMedia({
          worker,
          conversationId: props.conversationId,
          messageId: props.messageId,
          mediaId: attachment.mediaId,
          // The original is requested for a save; a preview variant would give
          // the user a downscaled copy of their own file without saying so.
          variant: save ? 'original' : 'preview',
        });
        if (save) {
          saveBlob(result.blob, attachment.filename ?? 'download');
          URL.revokeObjectURL(result.url);
        } else {
          setObjectUrl(result.url);
        }
      } catch {
        setError(t('media.downloadFailed'));
      } finally {
        setBusy(false);
      }
    },
    [attachment.filename, attachment.mediaId, props.conversationId, props.messageId, t, worker],
  );

  const startDownload = () => {
    if (props.protectedContent) {
      setError(t('media.protected'));
      return;
    }
    if (needsMeteredConfirmation(attachment.sizeBytes)) {
      setConfirming(true);
      return;
    }
    void download(true);
  };

  // A voice note gets the full player. The source is resolved lazily so that a
  // conversation full of voice messages does not request a grant for each.
  if (props.kind === Content_Kind.VOICE && ready) {
    const waveform = media?.variants.find((variant) => variant.waveform.length > 0)?.waveform ?? [];
    return (
      <div className={styles.attachments}>
        <VoicePlayer
          playbackKey={`${props.messageId}:${attachment.mediaId}`}
          waveform={waveform}
          durationMs={attachment.durationMs}
          {...(props.autoPlayVoice === undefined ? {} : { autoPlay: props.autoPlayVoice })}
          {...(props.onVoicePosition === undefined ? {} : { onPosition: props.onVoicePosition })}
          resolveSource={async () => {
            if (!worker) {
              throw new Error('offline');
            }
            const grant = await requestGrant({
              worker,
              conversationId: props.conversationId,
              messageId: props.messageId,
              mediaId: attachment.mediaId,
              variant: 'playback',
            });
            return grant.url;
          }}
        />
      </div>
    );
  }

  if (!ready) {
    return <p className={styles.placeholder}>{t('media.notReady')}</p>;
  }

  if (previewable && attachment.mime.startsWith('image/')) {
    return (
      <>
        {objectUrl === null ? (
          <button
            type="button"
            className={styles.fileRow}
            onClick={() => {
              void download(false);
            }}
            disabled={busy}
          >
            <span className={styles.fileBody}>
              <span className={styles.fileName} dir="auto">
                {attachment.filename ?? t('media.photo')}
              </span>
              <span className={styles.fileMeta}>
                {busy ? t('media.downloading') : formatBytes(attachment.sizeBytes)}
              </span>
            </span>
          </button>
        ) : (
          <img
            className={styles.image}
            src={objectUrl}
            // The filename is the only description the client has. It is used
            // rather than an invented one, because a wrong alt text is worse
            // than a plain one.
            alt={attachment.filename ?? t('media.photo')}
            // Verified metadata, so the layout does not shift when it decodes.
            {...(attachment.width === null ? {} : { width: attachment.width })}
            {...(attachment.height === null ? {} : { height: attachment.height })}
          />
        )}
        {error === null ? null : (
          <p className={styles.uploadError} role="status">
            {error}
          </p>
        )}
      </>
    );
  }

  if (previewable && attachment.mime.startsWith('video/')) {
    const circular = props.kind === Content_Kind.VIDEO_NOTE;
    return objectUrl === null ? (
      <button
        type="button"
        className={styles.fileRow}
        onClick={() => {
          void download(false);
        }}
        disabled={busy}
      >
        <span className={styles.fileBody}>
          <span className={styles.fileName} dir="auto">
            {attachment.filename ?? (circular ? t('media.videoNote') : t('media.video'))}
          </span>
          <span className={styles.fileMeta}>
            {busy ? t('media.downloading') : formatBytes(attachment.sizeBytes)}
          </span>
        </span>
      </button>
    ) : (
      <video
        className={circular ? styles.videoNote : styles.videoPlayer}
        src={objectUrl}
        controls
        playsInline
        aria-label={attachment.filename ?? t('media.video')}
      />
    );
  }

  return (
    <>
      <div className={styles.fileRow}>
        <span className={styles.fileBody}>
          <span className={styles.fileName} dir="auto">
            {attachment.filename ?? t('media.unknownFormat')}
          </span>
          <span className={styles.fileMeta}>
            {formatBytes(attachment.sizeBytes)}
            {failedProcessing ? ` · ${t('media.failed')}` : ''}
          </span>
        </span>
        <button
          type="button"
          className={styles.uploadAction}
          onClick={startDownload}
          disabled={busy || props.protectedContent}
        >
          {busy ? t('media.downloading') : t('media.download')}
        </button>
      </div>
      {error === null ? null : (
        <p className={styles.uploadError} role="status">
          {error}
        </p>
      )}

      {confirming ? (
        <div className={dialogStyles.backdrop}>
          <div
            className={dialogStyles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`metered-${attachment.mediaId}`}
          >
            <h2 id={`metered-${attachment.mediaId}`} className={dialogStyles.title}>
              {t('media.confirmTitle')}
            </h2>
            <p className={dialogStyles.description}>
              {t('media.confirmBody', {
                name: attachment.filename ?? t('media.unknownFormat'),
                size: formatBytes(attachment.sizeBytes),
              })}
            </p>
            <div className={dialogStyles.actions}>
              <button
                type="button"
                className={dialogStyles.cancel}
                onClick={() => {
                  setConfirming(false);
                }}
              >
                {t('media.confirmCancel')}
              </button>
              <button
                type="button"
                className={dialogStyles.confirm}
                onClick={() => {
                  setConfirming(false);
                  void download(true);
                }}
              >
                {t('media.confirmDownload')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
